import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

/**
 * URL search params for the add-contract OAuth flow (survives full page reload).
 *
 * - `add_contract=1` — add-contract sheet is open
 * - `channel_id` — selected Vonq channel id
 * - `channel_name` — display label for the board search input
 * - `hash` — OAuth callback token (removed when the sheet closes)
 */
export const ADD_CONTRACT_PARAM = 'add_contract';
export const ADD_CONTRACT_CHANNEL_ID_PARAM = 'channel_id';
export const ADD_CONTRACT_CHANNEL_NAME_PARAM = 'channel_name';
export const OAUTH_CALLBACK_HASH_PARAM = 'hash';

export function useAddContractSearchParams() {
  const [searchParams, setSearchParams] = useSearchParams();

  const isSheetOpen = searchParams.get(ADD_CONTRACT_PARAM) === '1';
  const channelName =
    searchParams.get(ADD_CONTRACT_CHANNEL_NAME_PARAM) ?? undefined;

  const channelId = useMemo(() => {
    const param = searchParams.get(ADD_CONTRACT_CHANNEL_ID_PARAM);
    if (param == null) return undefined;
    const id = Number(param);
    return Number.isFinite(id) ? id : undefined;
  }, [searchParams]);

  const setSheetOpen = useCallback(
    (open: boolean) => {
      setSearchParams(
        prev => {
          const next = new URLSearchParams(prev);
          if (open) {
            next.set(ADD_CONTRACT_PARAM, '1');
          } else {
            next.delete(ADD_CONTRACT_PARAM);
            next.delete(ADD_CONTRACT_CHANNEL_ID_PARAM);
            next.delete(ADD_CONTRACT_CHANNEL_NAME_PARAM);
            next.delete(OAUTH_CALLBACK_HASH_PARAM);
          }
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const setSelectedChannel = useCallback(
    (id: number | undefined, name?: string, openWithName = false) => {
      setSearchParams(
        prev => {
          const next = new URLSearchParams(prev);
          if (id == null && !openWithName) {
            next.delete(ADD_CONTRACT_CHANNEL_ID_PARAM);
            next.delete(ADD_CONTRACT_CHANNEL_NAME_PARAM);
            return next;
          }
          next.set(ADD_CONTRACT_PARAM, '1');
          if (id == null) next.delete(ADD_CONTRACT_CHANNEL_ID_PARAM);
          else next.set(ADD_CONTRACT_CHANNEL_ID_PARAM, String(id));
          if (name?.trim()) {
            next.set(ADD_CONTRACT_CHANNEL_NAME_PARAM, name);
          } else {
            next.delete(ADD_CONTRACT_CHANNEL_NAME_PARAM);
          }
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const openWithChannel = useCallback(
    (id: number | undefined, name: string) =>
      setSelectedChannel(id, name, true),
    [setSelectedChannel],
  );

  return {
    isSheetOpen,
    channelId,
    channelName,
    setSheetOpen,
    setSelectedChannel,
    openWithChannel,
  };
}
